import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { supabase } from '../integrations/supabase/client';
import { useToast } from '../hooks/use-toast';

export interface Client {
  id: string;
  name: string;
  email?: string | null;
  phone?: string | null;
  source: string;
  status: string;
  interests: string[];
  budget: { min: number; max: number };
  location: string | null;
  notes?: string | null;
  last_contact?: string | null;
  metadata?: any;
  assigned_to?: string | null;
  converted_from_lead_id?: string | null;
  converted_from_portal_lead_id?: string | null;
  created_at: string;
  updated_at: string;
}

interface ClientContextType {
  clients: Client[];
  loading: boolean;
  addClient: (client: Partial<Client>) => Promise<void>;
  updateClient: (client: Client) => Promise<void>;
  deleteClient: (id: string) => Promise<void>;
  getClientById: (id: string) => Client | undefined;
  getClientsByStatus: (status: string) => Client[];
  fetchClients: () => Promise<void>;
}

const ClientContext = createContext<ClientContextType | undefined>(undefined);

export const ClientProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  const fetchClients = async () => {
    try {
      setLoading(true);
      
      const { data: clientsData, error } = await supabase
        .from('clients')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Formatar dados e aplicar display de origem
      const formattedClients = (clientsData || []).map(client => ({
        ...client,
        interests: client.interests || [],
        location: client.location || '',
        budget: (client.budget && typeof client.budget === 'object' && 
                typeof (client.budget as any).min === 'number' && 
                typeof (client.budget as any).max === 'number') ? 
                client.budget as { min: number; max: number } : { min: 0, max: 0 },
        source: formatClientSource(client.source, client.metadata)
      }));

      setClients(formattedClients);
    } catch (error) {
      console.error('Erro ao carregar clientes:', error);
      toast({
        title: "Erro",
        description: "Não foi possível carregar os clientes",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const formatClientSource = (source: string, metadata: any) => {
    // Se há portal na metadata, usar isso
    if (metadata?.original_portal) {
      const portal = metadata.original_portal.toLowerCase();
      if (portal.includes('olx')) return 'Portal OLX';
      if (portal.includes('vivareal') || portal.includes('viva real')) return 'Portal Viva Real';
      if (portal.includes('zap')) return 'Portal ZAP Imóveis';
      return `Portal ${metadata.original_portal}`;
    }
    
    // Se source é olx, mostrar Portal OLX
    if (source === 'olx') return 'Portal OLX';
    if (source === 'website') return 'Site';
    if (source === 'whatsapp') return 'WhatsApp';
    if (source === 'facebook') return 'Facebook';
    if (source === 'instagram') return 'Instagram';
    if (source === 'manual') return 'Manual';
    
    return source || 'Manual';
  };

  const addClient = async (clientData: Partial<Client>) => {
    try {
      // Normalizar email se fornecido
      const normalizedEmail = clientData.email ? clientData.email.toLowerCase().trim() : null;
      
      if (normalizedEmail) {
        // Verificar se já existe cliente com este email
        const { data: existingClient } = await supabase
          .from('clients')
          .select()
          .eq('email', normalizedEmail)
          .maybeSingle();

        if (existingClient) {
          // Atualizar cliente existente
          const { data, error } = await supabase
            .from('clients')
            .update({
              name: clientData.name || existingClient.name,
              phone: clientData.phone || existingClient.phone,
              source: clientData.source || existingClient.source,
              status: clientData.status || existingClient.status,
              interests: clientData.interests || existingClient.interests,
              budget: clientData.budget || existingClient.budget,
              location: clientData.location || existingClient.location,
              notes: clientData.notes || existingClient.notes,
              metadata: Object.assign({}, existingClient.metadata || {}, clientData.metadata || {}),
              assigned_to: clientData.assigned_to || existingClient.assigned_to,
              converted_from_lead_id: clientData.converted_from_lead_id || existingClient.converted_from_lead_id,
              converted_from_portal_lead_id: clientData.converted_from_portal_lead_id || existingClient.converted_from_portal_lead_id,
              updated_at: new Date().toISOString()
            })
            .eq('id', existingClient.id)
            .select()
            .single();

          if (error) throw error;

          const formattedClient = {
            ...data,
            interests: data.interests || [],
            location: data.location || '',
            budget: (data.budget && typeof data.budget === 'object' && 
                    typeof (data.budget as any).min === 'number' && 
                    typeof (data.budget as any).max === 'number') ? 
                    data.budget as { min: number; max: number } : { min: 0, max: 0 },
            source: formatClientSource(data.source, data.metadata)
          };

          setClients(prev => prev.map(client => 
            client.id === formattedClient.id ? formattedClient : client
          ));

          toast({
            title: "Cliente atualizado",
            description: "Cliente já existia e foi atualizado com as novas informações",
          });
          return;
        }
      }

      // Criar novo cliente
      const { data, error } = await supabase
        .from('clients')
        .insert({
          name: clientData.name || 'Cliente sem nome',
          email: normalizedEmail,
          phone: clientData.phone || null,
          source: clientData.source || 'manual',
          status: clientData.status || 'prospect',
          interests: clientData.interests || [],
          budget: clientData.budget || { min: 0, max: 0 },
          location: clientData.location || '',
          notes: clientData.notes || null,
          last_contact: clientData.last_contact || null,
          metadata: clientData.metadata || {},
          assigned_to: clientData.assigned_to || null,
          converted_from_lead_id: clientData.converted_from_lead_id || null,
          converted_from_portal_lead_id: clientData.converted_from_portal_lead_id || null
        })
        .select()
        .single();

      if (error) throw error;

      // Adicionar à lista local com formatação
      const formattedClient = {
        ...data,
        interests: data.interests || [],
        location: data.location || '',
        budget: (data.budget && typeof data.budget === 'object' && 
                typeof (data.budget as any).min === 'number' && 
                typeof (data.budget as any).max === 'number') ? 
                data.budget as { min: number; max: number } : { min: 0, max: 0 },
        source: formatClientSource(data.source, data.metadata)
      };
      setClients(prev => [formattedClient, ...prev]);

      toast({
        title: "Cliente criado",
        description: "Cliente foi criado com sucesso",
      });
    } catch (error) {
      console.error('Erro ao criar cliente:', error);
      toast({
        title: "Erro",
        description: "Não foi possível criar o cliente",
        variant: "destructive",
      });
    }
  };

  const updateClient = async (updatedClient: Client) => {
    try {
      const { error } = await supabase
        .from('clients')
        .update({
          name: updatedClient.name,
          email: updatedClient.email,
          phone: updatedClient.phone,
          source: updatedClient.source,
          status: updatedClient.status,
          interests: updatedClient.interests,
          budget: updatedClient.budget,
          location: updatedClient.location,
          notes: updatedClient.notes,
          last_contact: updatedClient.last_contact,
          metadata: updatedClient.metadata,
          assigned_to: updatedClient.assigned_to,
          updated_at: new Date().toISOString()
        })
        .eq('id', updatedClient.id);

      if (error) throw error;

      // Atualizar na lista local
      setClients(prev => 
        prev.map(client => 
          client.id === updatedClient.id 
            ? { ...updatedClient, source: formatClientSource(updatedClient.source, updatedClient.metadata) }
            : client
        )
      );

      toast({
        title: "Cliente atualizado",
        description: "Cliente foi atualizado com sucesso",
      });
    } catch (error) {
      console.error('Erro ao atualizar cliente:', error);
      toast({
        title: "Erro",
        description: "Não foi possível atualizar o cliente",
        variant: "destructive",
      });
    }
  };

  const deleteClient = async (id: string) => {
    try {
      const { error } = await supabase
        .from('clients')
        .delete()
        .eq('id', id);

      if (error) throw error;

      setClients(prev => prev.filter(client => client.id !== id));

      toast({
        title: "Cliente excluído",
        description: "Cliente foi excluído com sucesso",
      });
    } catch (error) {
      console.error('Erro ao deletar cliente:', error);
      toast({
        title: "Erro",
        description: "Não foi possível excluir o cliente",
        variant: "destructive",
      });
    }
  };

  const getClientById = (id: string) => {
    return clients.find(client => client.id === id);
  };

  const getClientsByStatus = (status: string) => {
    return clients.filter(client => client.status === status);
  };

  useEffect(() => {
    fetchClients();

    // Escutar mudanças em tempo real
    const channel = supabase
      .channel('clients_changes')
      .on('postgres_changes', 
        { event: 'INSERT', schema: 'public', table: 'clients' },
        () => fetchClients()
      )
      .on('postgres_changes', 
        { event: 'UPDATE', schema: 'public', table: 'clients' },
        () => fetchClients()
      )
      .on('postgres_changes', 
        { event: 'DELETE', schema: 'public', table: 'clients' },
        () => fetchClients()
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const value = {
    clients,
    loading,
    addClient,
    updateClient,
    deleteClient,
    getClientById,
    getClientsByStatus,
    fetchClients
  };

  return (
    <ClientContext.Provider value={value}>
      {children}
    </ClientContext.Provider>
  );
};

export const useClient = () => {
  const context = useContext(ClientContext);
  if (context === undefined) {
    throw new Error('useClient must be used within a ClientProvider');
  }
  return context;
};